import { useState, useCallback, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog';
import {
    Users, Search, Briefcase, Plus, Eye, Send, Calendar, Play, Pause,
    XCircle, ChevronLeft, MapPin
} from 'lucide-react';

const STATUS_TABS = [
    { key: 'all', label: 'All' },
    { key: 'applied', label: 'Applied' },
    { key: 'reviewed', label: 'Reviewed' },
    { key: 'shortlisted', label: 'Shortlisted' },
    { key: 'offer_sent', label: 'Offer Sent' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'withdrawn', label: 'Withdrawn' },
];

const STATUS_BADGE = {
    applied: { label: 'Applied', cls: 'text-blue-700 border-blue-300 bg-blue-50' },
    reviewed: { label: 'Reviewed', cls: 'text-amber-700 border-amber-300 bg-amber-50' },
    shortlisted: { label: 'Shortlisted', cls: 'text-purple-700 border-purple-300 bg-purple-50' },
    offer_sent: { label: 'Offer Sent', cls: 'text-green-700 border-green-300 bg-green-50' },
    rejected: { label: 'Rejected', cls: 'text-red-600 border-red-300 bg-red-50' },
    withdrawn: { label: 'Withdrawn', cls: 'text-zinc-500 border-zinc-300 bg-zinc-50' },
};

function JobStatusBadges({ job }) {
    const visibility = job.visibility || 'public';
    const status = job.status || 'active';
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <Badge
                variant="outline"
                className={`rounded-none text-[10px] ${visibility === 'public' ? 'text-emerald-700 border-emerald-300 bg-emerald-50' : 'text-zinc-600 border-zinc-300 bg-zinc-50'}`}
                data-testid={`job-visibility-${job.id}`}
            >
                {visibility === 'public' ? 'Public' : 'Private'}
            </Badge>
            <Badge
                variant="outline"
                className={`rounded-none text-[10px] capitalize ${
                    status === 'active' ? 'text-blue-700 border-blue-300 bg-blue-50' :
                    status === 'paused' ? 'text-amber-700 border-amber-300 bg-amber-50' :
                    'text-zinc-500 border-zinc-300 bg-zinc-50'
                }`}
                data-testid={`job-status-${job.id}`}
            >
                {status}
            </Badge>
            <Badge variant="outline" className="rounded-none text-[10px]" data-testid={`job-applicant-count-${job.id}`}>
                <Users className="w-3 h-3 mr-1" /> {job.applicant_count || 0}
            </Badge>
        </div>
    );
}

function ApplicantsPanel({
    job,
    onBack,
    onOpenWorkerProfile,
    onSendOfferToWorker,
    onScheduleInterviewWithWorker,
    onApplicationsChanged,
    refreshKey,
}) {
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [nameSearch, setNameSearch] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/employer/jobs/${job.id}/applicants`);
            setApplicants(data.applicants || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [job.id]);

    useEffect(() => { load(); }, [load, refreshKey]);

    // Refresh applicants when the parent dashboard signals that applications
    // may have changed (e.g. after a successful Send Offer that flips the
    // matched application to offer_sent).
    useEffect(() => {
        const handler = (e) => {
            const jobId = e?.detail?.jobId;
            if (!jobId || jobId === job.id) load();
        };
        window.addEventListener('gulfbridge:applications-changed', handler);
        return () => window.removeEventListener('gulfbridge:applications-changed', handler);
    }, [job.id, load]);

    const patchStatus = async (app, newStatus) => {
        setBusyId(app.id);
        try {
            await api.patch(`/employer/jobs/${job.id}/applicants/${app.id}/status`, { status: newStatus });
            await load();
            if (onApplicationsChanged) onApplicationsChanged();
        } catch (err) {
            console.error('status update failed:', err);
        } finally {
            setBusyId(null);
        }
    };

    const viewProfile = async (app) => {
        // Auto-mark Reviewed on first open (per spec), then open the profile.
        if (app.status === 'applied') {
            try {
                await api.patch(`/employer/jobs/${job.id}/applicants/${app.id}/status`, { status: 'reviewed' });
                await load();
            } catch (err) { console.error(err); }
        }
        if (onOpenWorkerProfile) onOpenWorkerProfile(app.worker_id);
    };

    const sendOffer = (app) => {
        if (onSendOfferToWorker) onSendOfferToWorker(app.worker_id, { fromJobId: job.id });
    };
    const scheduleInterview = (app) => {
        if (onScheduleInterviewWithWorker) onScheduleInterviewWithWorker(app.worker_id);
    };

    const filtered = applicants.filter(a => {
        if (statusFilter !== 'all' && a.status !== statusFilter) return false;
        if (nameSearch && !(a.worker?.name || '').toLowerCase().includes(nameSearch.toLowerCase())) return false;
        return true;
    });

    return (
        <div data-testid={`applicants-panel-${job.id}`}>
            <div className="flex items-center justify-between mb-4">
                <Button variant="ghost" onClick={onBack} className="rounded-none h-9 px-3 text-xs uppercase tracking-[0.15em] font-bold" data-testid="applicants-back-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back to Jobs
                </Button>
                <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-bold">
                    {filtered.length} of {applicants.length}
                </div>
            </div>

            <div className="border border-zinc-200 p-4 mb-4">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold mb-1">Job</p>
                <h3 className="font-heading text-lg font-bold" data-testid="applicants-job-title">{job.title}</h3>
                <p className="text-sm text-zinc-500">{job.location}, {job.country} · {job.currency || 'USD'} {job.salary}</p>
                <div className="mt-2"><JobStatusBadges job={job} /></div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                        placeholder="Search applicants by name..."
                        value={nameSearch}
                        onChange={e => setNameSearch(e.target.value)}
                        className="pl-10 h-11 rounded-none"
                        data-testid="applicants-search-input"
                    />
                </div>
            </div>

            <div className="flex gap-2 flex-wrap mb-4" data-testid="applicants-status-filters">
                {STATUS_TABS.map(t => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setStatusFilter(t.key)}
                        className={`h-9 px-3 text-xs font-semibold border transition-colors ${statusFilter === t.key ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 hover:border-zinc-400 text-zinc-700'}`}
                        data-testid={`applicant-status-filter-${t.key}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent animate-spin" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-12 border border-zinc-200" data-testid="applicants-empty">
                    <Users className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                    <p className="font-heading font-bold text-base mb-1">No applicants</p>
                    <p className="text-sm text-zinc-500">
                        {applicants.length === 0 ? 'No workers have applied to this job yet.' : 'No applicants match the current filters.'}
                    </p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="applicants-list">
                    {filtered.map(app => {
                        const w = app.worker || {};
                        const badge = STATUS_BADGE[app.status] || { label: app.status, cls: 'text-zinc-600 border-zinc-300' };
                        const disableActions = app.status === 'withdrawn' || app.status === 'offer_sent';
                        return (
                            <div key={app.id} className="border border-zinc-200 p-4" data-testid={`applicant-${app.id}`}>
                                <div className="flex items-start justify-between mb-2 gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="font-semibold text-base truncate" data-testid={`applicant-name-${app.id}`}>{w.name || 'Worker'}</h4>
                                            <Badge variant="outline" className={`rounded-none text-[10px] ${badge.cls}`} data-testid={`applicant-status-${app.id}`}>
                                                {badge.label}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {w.country || '—'}</span>
                                            {w.experience && <span>· {w.experience}</span>}
                                        </p>
                                        {Array.isArray(w.interests) && w.interests.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {w.interests.slice(0, 4).map(s => (
                                                    <Badge key={s} variant="outline" className="rounded-none text-[10px]">{s}</Badge>
                                                ))}
                                            </div>
                                        )}
                                        <p className="text-[10px] text-zinc-400 mt-2">Applied {app.applied_at ? new Date(app.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</p>
                                    </div>
                                </div>
                                <Separator className="my-3" />
                                <div className="flex gap-2 flex-wrap">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="rounded-none text-xs h-8 border-zinc-900"
                                        onClick={() => viewProfile(app)}
                                        data-testid={`applicant-view-profile-${app.id}`}
                                    >
                                        <Eye className="w-3 h-3 mr-1" /> View Profile
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="rounded-none text-xs h-8 border-zinc-900"
                                        onClick={() => scheduleInterview(app)}
                                        disabled={disableActions}
                                        data-testid={`applicant-schedule-${app.id}`}
                                    >
                                        <Calendar className="w-3 h-3 mr-1" /> Schedule Interview
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="rounded-none text-xs h-8 bg-zinc-900 text-white"
                                        onClick={() => sendOffer(app)}
                                        disabled={disableActions}
                                        data-testid={`applicant-send-offer-${app.id}`}
                                    >
                                        <Send className="w-3 h-3 mr-1" /> Send Offer
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="rounded-none text-xs h-8 text-red-600 hover:text-red-700"
                                        onClick={() => setRejectTarget(app)}
                                        disabled={busyId === app.id || disableActions || app.status === 'rejected'}
                                        data-testid={`applicant-reject-${app.id}`}
                                    >
                                        <XCircle className="w-3 h-3 mr-1" /> Reject
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <AlertDialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
                <AlertDialogContent className="rounded-none border-2 border-zinc-900">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reject application?</AlertDialogTitle>
                        <AlertDialogDescription>
                            The applicant will be notified that they were not selected for this job. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="confirm-reject-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-none bg-red-600 hover:bg-red-700 text-white"
                            onClick={async () => {
                                const a = rejectTarget;
                                setRejectTarget(null);
                                if (a) await patchStatus(a, 'rejected');
                            }}
                            data-testid="confirm-reject-btn"
                        >
                            Reject
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default function EmployerJobBoard({
    jobs,
    onCreateJob,
    onEditJob,
    onDeleteJob,
    onRefresh,
    onOpenWorkerProfile,
    onSendOfferToWorker,
    onScheduleInterviewWithWorker,
    initialJobId,
}) {
    const [applicantsJob, setApplicantsJob] = useState(null);
    const [busyJobId, setBusyJobId] = useState(null);
    const [closeTarget, setCloseTarget] = useState(null);
    const [applicantsRefreshKey, setApplicantsRefreshKey] = useState(0);

    // Deep-link: when a specific job's applicants should be opened (e.g. from
    // a notification action).
    useEffect(() => {
        if (initialJobId && jobs?.length) {
            const j = jobs.find(x => x.id === initialJobId);
            if (j) setApplicantsJob(j);
        }
    }, [initialJobId, jobs]);

    // Keep applicantsJob fresh when the parent list refreshes.
    useEffect(() => {
        if (applicantsJob) {
            const fresh = jobs.find(j => j.id === applicantsJob.id);
            if (fresh) setApplicantsJob(fresh);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs]);

    const patchJob = async (job, patch) => {
        setBusyJobId(job.id);
        try {
            await api.put(`/employer/jobs/${job.id}`, patch);
            if (onRefresh) await onRefresh();
        } catch (err) {
            console.error('job update failed:', err);
        } finally {
            setBusyJobId(null);
        }
    };

    if (applicantsJob) {
        return (
            <ApplicantsPanel
                job={applicantsJob}
                onBack={() => setApplicantsJob(null)}
                onOpenWorkerProfile={onOpenWorkerProfile}
                onSendOfferToWorker={onSendOfferToWorker}
                onScheduleInterviewWithWorker={onScheduleInterviewWithWorker}
                onApplicationsChanged={onRefresh}
                refreshKey={applicantsRefreshKey}
            />
        );
    }

    return (
        <div data-testid="employer-job-board">
            <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-bold">Your Posted Jobs</h3>
                <Button className="rounded-none bg-zinc-900 text-white h-10 font-semibold" onClick={onCreateJob} data-testid="create-job-btn">
                    <Plus className="w-4 h-4 mr-2" /> Create Job
                </Button>
            </div>

            {(!jobs || jobs.length === 0) ? (
                <div className="text-center py-12 border border-zinc-200" data-testid="jobs-empty">
                    <Briefcase className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                    <p className="font-heading font-bold text-base mb-1">No job listings yet</p>
                    <p className="text-sm text-zinc-500 max-w-md mx-auto mb-4">You have not created any job listings yet. Create your first job to start attracting applicants and sending offers.</p>
                    <Button className="rounded-none bg-zinc-900 text-white" onClick={onCreateJob} data-testid="empty-create-job-btn">
                        <Plus className="w-4 h-4 mr-2" /> Create your first job
                    </Button>
                </div>
            ) : (
                <div className="space-y-3" data-testid="jobs-list">
                    {jobs.map(j => {
                        const status = j.status || 'active';
                        const isClosed = status === 'closed';
                        const isPaused = status === 'paused';
                        return (
                            <div key={j.id} className="border border-zinc-200 p-5" data-testid={`job-${j.id}`}>
                                <div className="flex items-start justify-between mb-2 gap-3">
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-semibold text-base truncate">{j.title}</h4>
                                        <p className="text-sm text-zinc-500">{j.location}, {j.country} · {j.currency || 'USD'} {j.salary}</p>
                                        <div className="mt-2"><JobStatusBadges job={j} /></div>
                                    </div>
                                </div>
                                {j.benefits && <p className="text-xs text-zinc-500 mb-1"><span className="font-semibold">Benefits:</span> {j.benefits}</p>}
                                {j.description && <p className="text-sm text-zinc-600 mt-2 line-clamp-3">{j.description}</p>}
                                <Separator className="my-3" />
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="rounded-none text-xs h-8 border-zinc-900"
                                        onClick={() => setApplicantsJob(j)}
                                        data-testid={`view-applicants-${j.id}`}
                                    >
                                        <Users className="w-3 h-3 mr-1" /> View Applicants ({j.applicant_count || 0})
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="rounded-none text-xs h-8"
                                        onClick={() => onEditJob(j)}
                                        disabled={isClosed}
                                        data-testid={`edit-job-${j.id}`}
                                    >
                                        Edit
                                    </Button>
                                    {!isClosed && (isPaused ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-none text-xs h-8"
                                            onClick={() => patchJob(j, { status: 'active' })}
                                            disabled={busyJobId === j.id}
                                            data-testid={`resume-job-${j.id}`}
                                        >
                                            <Play className="w-3 h-3 mr-1" /> Resume
                                        </Button>
                                    ) : (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-none text-xs h-8"
                                            onClick={() => patchJob(j, { status: 'paused' })}
                                            disabled={busyJobId === j.id}
                                            data-testid={`pause-job-${j.id}`}
                                        >
                                            <Pause className="w-3 h-3 mr-1" /> Pause
                                        </Button>
                                    ))}
                                    {!isClosed && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="rounded-none text-xs h-8 text-red-600 hover:text-red-700"
                                            onClick={() => setCloseTarget(j)}
                                            disabled={busyJobId === j.id}
                                            data-testid={`close-job-${j.id}`}
                                        >
                                            <XCircle className="w-3 h-3 mr-1" /> Close
                                        </Button>
                                    )}
                                    {onDeleteJob && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="rounded-none text-xs h-8 text-red-500 hover:text-red-700 ml-auto"
                                            onClick={() => onDeleteJob(j.id)}
                                            data-testid={`delete-job-${j.id}`}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <AlertDialog open={!!closeTarget} onOpenChange={(o) => !o && setCloseTarget(null)}>
                <AlertDialogContent className="rounded-none border-2 border-zinc-900">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Close this job?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Closing removes the job from public listings and prevents new applications. Once closed, a job cannot be re-opened — you'll need to create a new listing instead.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="confirm-close-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-none bg-red-600 hover:bg-red-700 text-white"
                            onClick={async () => {
                                const j = closeTarget;
                                setCloseTarget(null);
                                if (j) await patchJob(j, { status: 'closed' });
                            }}
                            data-testid="confirm-close-job-btn"
                        >
                            Close Job
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
